import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, gql, compose } from 'react-apollo';
import { toastr } from 'react-redux-toastr';

// Style
import cx from 'classnames';
import {
    Button,
    Row,
    FormGroup,
    Col,
    FormControl,
    Panel,
    Tabs,
    Tab,
} from 'react-bootstrap';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import s from './CancellationPolicySettings.css';
import cs from '../../../components/commonStyle.css';

// Translation
import { FormattedMessage, injectIntl } from 'react-intl';
import messages from '../../../locale/messages';
import Loader from '../../../components/Loader';

// Query to get all cancellation policies
const GET_ALL_CANCELLATION = gql`
  query getAllCancellation {
    getAllCancellation {
      id
      policyName
      priorDays
      accommodationPriorCheckIn
      accommodationBeforeCheckIn
      accommodationDuringCheckIn
      guestFeePriorCheckIn
      guestFeeBeforeCheckIn
      guestFeeDuringCheckIn
      isEnable
    }
  }
`;

// Mutation to update a policy
const UPDATE_CANCELLATION_SETTINGS = gql`
  mutation updateCancellationSettings(
    $id: Int!
    $priorDays: Int
    $accommodationPriorCheckIn: Float
    $accommodationBeforeCheckIn: Float
    $accommodationDuringCheckIn: Float
    $guestFeePriorCheckIn: Float
    $guestFeeBeforeCheckIn: Float
    $guestFeeDuringCheckIn: Float
  ) {
    updateCancellationSettings(
      id: $id
      priorDays: $priorDays
      accommodationPriorCheckIn: $accommodationPriorCheckIn
      accommodationBeforeCheckIn: $accommodationBeforeCheckIn
      accommodationDuringCheckIn: $accommodationDuringCheckIn
      guestFeePriorCheckIn: $guestFeePriorCheckIn
      guestFeeBeforeCheckIn: $guestFeeBeforeCheckIn
      guestFeeDuringCheckIn: $guestFeeDuringCheckIn
    ) {
      id
      status
    }
  }
`;

class CancellationPolicySettings extends Component {
    static propTypes = {
        title: PropTypes.string,
        data: PropTypes.object,
        policyType: PropTypes.string,
        onPolicyChange: PropTypes.func,
    };

    constructor(props) {
        super(props);
        this.state = {
            saving: false,
            policies: {}
        };
    }

    componentDidUpdate(prevProps) {
        const { data } = this.props;
        if (data?.getAllCancellation && !prevProps.data?.getAllCancellation) {
            // Initialize form with data from GraphQL
            const policies = {};
            data.getAllCancellation.forEach(policy => {
                policies[policy.id] = { ...policy };
            });
            this.setState({ policies });
        }
    }

    handleFieldChange = (policyId, field, value) => {
        const numValue = parseFloat(value) || 0;
        this.setState(prevState => ({
            policies: {
                ...prevState.policies,
                [policyId]: {
                    ...prevState.policies[policyId],
                    [field]: numValue
                }
            }
        }));
    };

    handleSave = async () => {
        const { mutate, data } = this.props;
        const { policies } = this.state;

        this.setState({ saving: true });

        try {
            for (const policyId of Object.keys(policies)) {
                const policy = policies[policyId];
                await mutate({
                    variables: {
                        id: parseInt(policyId),
                        priorDays: parseInt(policy.priorDays),
                        accommodationPriorCheckIn: parseFloat(policy.accommodationPriorCheckIn),
                        accommodationBeforeCheckIn: parseFloat(policy.accommodationBeforeCheckIn),
                        accommodationDuringCheckIn: parseFloat(policy.accommodationDuringCheckIn),
                        guestFeePriorCheckIn: parseFloat(policy.guestFeePriorCheckIn),
                        guestFeeBeforeCheckIn: parseFloat(policy.guestFeeBeforeCheckIn),
                        guestFeeDuringCheckIn: parseFloat(policy.guestFeeDuringCheckIn),
                    }
                });
            }

            toastr.success('Éxito', 'Políticas actualizadas correctamente');
            data.refetch();
        } catch (error) {
            console.error('Error saving policies:', error);
            toastr.error('Error', 'No se pudieron guardar las políticas');
        }

        this.setState({ saving: false });
    };

    renderPolicyCard = (policy) => {
        const { policies } = this.state;
        const currentPolicy = policies[policy.id] || policy;

        const colorClass = {
            'Flexible': s.flexibleCard,
            'Moderate': s.moderateCard,
            'Strict': s.strictCard
        }[policy.policyName] || '';

        return (
            <Panel key={policy.id} className={cx(s.policyPanel, colorClass)}>
                <Panel.Heading>
                    <Panel.Title componentClass="h3" className={s.policyTitle}>
                        {policy.policyName === 'Flexible' && '📗 '}
                        {policy.policyName === 'Moderate' && '📙 '}
                        {policy.policyName === 'Strict' && '📕 '}
                        {policy.policyName}
                    </Panel.Title>
                </Panel.Heading>
                <Panel.Body>
                    <Row>
                        {/* Days Section */}
                        <Col xs={12} sm={4} md={4}>
                            <FormGroup>
                                <label className={cs.labelTextNew}>
                                    Días de Gracia
                                </label>
                                <FormControl
                                    type="number"
                                    min="0"
                                    value={currentPolicy.priorDays || 0}
                                    onChange={(e) => this.handleFieldChange(policy.id, 'priorDays', e.target.value)}
                                    className={cs.formControlInput}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    {/* Accommodation Refund Section */}
                    <div className={s.sectionDivider}>
                        <h5 className={s.sectionTitle}>% Reembolso Alojamiento</h5>
                        <Row>
                            <Col xs={12} sm={4} md={4}>
                                <FormGroup>
                                    <label className={cs.labelTextNew}>A Tiempo (Prior)</label>
                                    <FormControl
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={currentPolicy.accommodationPriorCheckIn || 0}
                                        onChange={(e) => this.handleFieldChange(policy.id, 'accommodationPriorCheckIn', e.target.value)}
                                        className={cs.formControlInput}
                                    />
                                </FormGroup>
                            </Col>
                            <Col xs={12} sm={4} md={4}>
                                <FormGroup>
                                    <label className={cs.labelTextNew}>Tarde (Before)</label>
                                    <FormControl
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={currentPolicy.accommodationBeforeCheckIn || 0}
                                        onChange={(e) => this.handleFieldChange(policy.id, 'accommodationBeforeCheckIn', e.target.value)}
                                        className={cs.formControlInput}
                                    />
                                </FormGroup>
                            </Col>
                            <Col xs={12} sm={4} md={4}>
                                <FormGroup>
                                    <label className={cs.labelTextNew}>Durante Viaje</label>
                                    <FormControl
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={currentPolicy.accommodationDuringCheckIn || 0}
                                        onChange={(e) => this.handleFieldChange(policy.id, 'accommodationDuringCheckIn', e.target.value)}
                                        className={cs.formControlInput}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </div>

                    {/* Guest Service Fee Section */}
                    <div className={s.sectionDivider}>
                        <h5 className={s.sectionTitle}>% Reembolso Service Fee</h5>
                        <Row>
                            <Col xs={12} sm={4} md={4}>
                                <FormGroup>
                                    <label className={cs.labelTextNew}>A Tiempo (Prior)</label>
                                    <FormControl
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={currentPolicy.guestFeePriorCheckIn || 0}
                                        onChange={(e) => this.handleFieldChange(policy.id, 'guestFeePriorCheckIn', e.target.value)}
                                        className={cs.formControlInput}
                                    />
                                </FormGroup>
                            </Col>
                            <Col xs={12} sm={4} md={4}>
                                <FormGroup>
                                    <label className={cs.labelTextNew}>Tarde (Before)</label>
                                    <FormControl
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={currentPolicy.guestFeeBeforeCheckIn || 0}
                                        onChange={(e) => this.handleFieldChange(policy.id, 'guestFeeBeforeCheckIn', e.target.value)}
                                        className={cs.formControlInput}
                                    />
                                </FormGroup>
                            </Col>
                            <Col xs={12} sm={4} md={4}>
                                <FormGroup>
                                    <label className={cs.labelTextNew}>Durante Viaje</label>
                                    <FormControl
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={currentPolicy.guestFeeDuringCheckIn || 0}
                                        onChange={(e) => this.handleFieldChange(policy.id, 'guestFeeDuringCheckIn', e.target.value)}
                                        className={cs.formControlInput}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </div>
                </Panel.Body>
            </Panel>
        );
    };

    render() {
        const { data, title, intl: { formatMessage } } = this.props;
        const { saving } = this.state;

        if (data?.loading) {
            return <Loader type={"text"} />;
        }

        const policies = data?.getAllCancellation || [];

        return (
            <div className={cx(s.pagecontentWrapper)}>
                <div className={s.sectionCenter}>
                    <div className={cs.commonAdminBorderSection}>
                        <h1 className={cx(cs.commonTotalText, cs.fontWeightBold, cs.spaceBottom12)}>
                            <FormattedMessage {...messages.cancellationPolicies} />
                        </h1>

                        {/* Policy Type Tabs */}
                        <Tabs
                            activeKey={this.props.policyType || 'Settings'}
                            onSelect={(key) => this.props.onPolicyChange && this.props.onPolicyChange(key)}
                            id="cancellation-policy-settings-tabs"
                            className={cx(s.policyTabs, 'cancellationPolicyTabs')}
                        >
                            <Tab eventKey="Settings" title="⚙️ Settings" />
                            <Tab eventKey="Flexible" title={formatMessage(messages.flexible)} />
                            <Tab eventKey="Moderate" title={formatMessage(messages.moderate)} />
                            <Tab eventKey="Strict" title={formatMessage(messages.strict)} />
                            <Tab eventKey="Shared" title="Shared" />
                        </Tabs>

                        <h2 className={cx(cs.commonSubTitleText, cs.fontWeightBold, cs.spaceTop4)}>
                            ⚙️ Configuración de Valores
                        </h2>

                        <p className={s.description}>
                            Configura los días de gracia y porcentajes de reembolso para cada política.
                            Estos valores afectan directamente los cálculos de cancelación.
                        </p>

                        {policies.map(policy => this.renderPolicyCard(policy))}

                        <div className={cx(s.saveSection, cs.spaceTop4)}>
                            <Button
                                className={cx(cs.btnPrimary, cs.btnlarge)}
                                onClick={this.handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Guardando...' : '💾 Guardar Cambios'}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default compose(
    withStyles(s, cs),
    graphql(GET_ALL_CANCELLATION, {
        options: {
            fetchPolicy: 'network-only'
        }
    }),
    graphql(UPDATE_CANCELLATION_SETTINGS)
)(injectIntl(CancellationPolicySettings));
